/**
 * Profile 域 — API 接口
 */

import { http } from '../../api/http';
import type { MeasurementRegion, SupportedLocale } from '../../i18n/locale';

// ── 类型定义 ──

export interface UserProfile {
  id: string;
  name: string;
  phone?: string;
  avatar?: string;
  role: string;
  createdAt: string;
  onboardingCompleted?: boolean;
  onboardingData?: Record<string, unknown>;
  preferredLocale?: SupportedLocale;
  measurementRegion?: MeasurementRegion;
}

/** 可更新的用户资料字段（严格限制，避免传入只读字段） */
export interface UpdateProfilePayload {
  name?: string;
  avatar?: string;
  onboardingCompleted?: boolean;
  onboardingData?: Record<string, unknown>;
  preferredLocale?: SupportedLocale;
  measurementRegion?: MeasurementRegion;
}

export interface ProfileCompleteness {
  percentage: number;
  missingFields: string[];
  suggestions: string[];
}

export interface MembershipPlan {
  id: string;
  name: string;
  tier: string;
  priceCents: number;
  durationMonths: number;
  features: unknown;
  isActive: boolean;
  createdAt?: string;
}

export interface MembershipOffer {
  id: string;
  planId: string;
  offerType: string;
  displayPriceCents: number;
  discountCents: number;
  eligibility: Record<string, unknown>;
  status: string;
}

export interface MembershipStatus {
  isActive: boolean;
  membership: {
    id?: string;
    planId?: string;
    status?: string;
    endDate?: string;
    autoRenew?: boolean;
  } | null;
  plan: MembershipPlan | null;
  daysRemaining?: number;
  features?: string[];
}

export interface MembershipOrder {
  id: string;
  planId: string;
  amount: string;
  currency: string;
  status: string;
  provider: string;
  providerOrderId?: string | null;
  offerId?: string | null;
  listAmount?: string | null;
  discountAmount?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMembershipOrderResponse {
  order: MembershipOrder;
  payment: Record<string, unknown>;
  plan: {
    id: string;
    name: string;
    features?: unknown;
    durationDays?: number | null;
  };
}

export interface RedeemMembershipResponse {
  redeemed: boolean;
  membershipId: string;
  redemptionId: string;
  planId: string;
  grantDays: number;
  expiresAt: string;
}

export interface ChannelAccountProfile {
  id: string;
  channel: 'wechat_miniapp' | 'feishu_bot';
  accountRowId?: string;
  providerId: string;
  providerAppId?: string | null;
  tenantKey?: string | null;
  capabilityFlags: string[];
  status: 'active' | 'revoked' | 'disabled';
  lastSeenAt?: string | null;
  revokedAt?: string | null;
}

export interface ChannelNotificationPreference {
  id: string;
  channelAccountProfileId: string;
  channel: 'wechat_miniapp' | 'feishu_bot';
  purpose: string;
  destinationScope: string;
  enabled: boolean;
  consentVersion: string;
}

export interface RevokeChannelResponse {
  revoked: boolean;
  cancelledOutboxCount: number;
}

export interface McpApiKeySummary {
  id: string;
  name: string;
  keyPrefix: string;
  keyLast4: string;
  maskedKey: string;
  scopes: string[];
  active: boolean;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

export interface CreateMcpApiKeyPayload {
  name?: string;
  scopes?: string[];
  expiresAt?: string | null;
}

export interface CreateMcpApiKeyResponse {
  key: string;
  apiKey: McpApiKeySummary;
}

export interface RevokeMcpApiKeyResponse {
  revoked: boolean;
  id: string;
}

// ── API 调用 ──

export const profileApi = {
  /** 获取当前用户信息 */
  async getMe(): Promise<UserProfile> {
    return http.get<UserProfile>('/users/me');
  },

  /** 更新用户信息 */
  async updateMe(data: UpdateProfilePayload): Promise<UserProfile> {
    return http.put<UserProfile>('/users/me', data);
  },

  /** 获取档案完善度 */
  async getCompleteness(): Promise<ProfileCompleteness> {
    return http.get<ProfileCompleteness>('/profile/completeness');
  },

  /** 会员套餐与 offer */
  async getMembershipPlans(): Promise<{ plans: MembershipPlan[]; offers: MembershipOffer[] }> {
    return http.get<{ plans: MembershipPlan[]; offers: MembershipOffer[] }>('/membership/plans');
  },

  /** 当前会员权益 */
  async getMembershipStatus(): Promise<MembershipStatus> {
    return http.get<MembershipStatus>('/membership/status');
  },

  /** 创建会员订单 */
  async createMembershipOrder(data: { planId: string; offerId?: string; provider: string; idempotencyKey?: string }) {
    return http.post<CreateMembershipOrderResponse>('/membership/orders', data);
  },

  /** 兑换会员码 */
  async redeemMembership(code: string) {
    return http.post<RedeemMembershipResponse>('/membership/redeem', { code });
  },

  /** 微信/飞书通道状态 */
  async getIntegrations(): Promise<{
    profiles: ChannelAccountProfile[];
    preferences: ChannelNotificationPreference[];
  }> {
    return http.get<{ profiles: ChannelAccountProfile[]; preferences: ChannelNotificationPreference[] }>(
      '/integrations/channels',
    );
  },

  /** 绑定微信小程序通道 */
  async bindWechat(data: { code: string; providerAppId?: string }): Promise<ChannelAccountProfile> {
    return http.post<ChannelAccountProfile>('/integrations/wechat/bind', data);
  },

  /** 撤销某个微信/飞书通道 */
  async revokeChannel(id: string): Promise<RevokeChannelResponse> {
    return http.delete<RevokeChannelResponse>(`/integrations/channels/${id}`);
  },

  /** 更新某个通道的通知偏好 */
  async updateChannelPreferences(
    id: string,
    preferences: Array<{ purpose: string; destinationScope: string; enabled: boolean; consentVersion: string }>,
  ): Promise<ChannelNotificationPreference[]> {
    return http.put<ChannelNotificationPreference[]>(`/integrations/channels/${id}/preferences`, { preferences });
  },

  /** MCP API Key 列表 */
  async getMcpApiKeys(): Promise<{ keys: McpApiKeySummary[] }> {
    return http.get<{ keys: McpApiKeySummary[] }>('/mcp/keys');
  },

  /** 创建 MCP API Key，完整 key 只返回一次 */
  async createMcpApiKey(data: CreateMcpApiKeyPayload): Promise<CreateMcpApiKeyResponse> {
    return http.post<CreateMcpApiKeyResponse>('/mcp/keys', data);
  },

  /** 撤销 MCP API Key */
  async revokeMcpApiKey(id: string): Promise<RevokeMcpApiKeyResponse> {
    return http.delete<RevokeMcpApiKeyResponse>(`/mcp/keys/${encodeURIComponent(id)}`);
  },
};
